import React, { useState, useEffect, useRef } from 'react';
import { resolveCoverUrl, getWorkerApiBase } from '../utils/imageCompressor.js';

// Module-level in-memory cache to prevent avatar skeleton flash on re-renders and page navigation
const loadedAvatarsCache = new Set<string>();

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface LazyAvatarProps {
  src?: string | null;
  alt: string;
  size?: AvatarSize;
  className?: string;
  fallbackText?: string;
  fallbackIcon?: React.ReactNode;
  shape?: 'rounded' | 'circle' | 'square';
  priority?: boolean;
}

const sizeClasses: Record<AvatarSize, { container: string; text: string }> = {
  xs: { container: 'w-6 h-6', text: 'text-[10px]' },
  sm: { container: 'w-8 h-8', text: 'text-xs' },
  md: { container: 'w-10 h-10', text: 'text-sm' },
  lg: { container: 'w-14 h-14', text: 'text-lg' },
  xl: { container: 'w-16 h-16', text: 'text-2xl' },
  '2xl': { container: 'w-20 h-20', text: 'text-3xl' },
};

export const LazyAvatar: React.FC<LazyAvatarProps> = ({
  src,
  alt,
  size = 'md',
  className = '',
  fallbackText,
  fallbackIcon,
  shape = 'rounded',
  priority = false,
}) => {
  const resolvedUrl = src ? resolveCoverUrl(src) : null;
  const [activeUrl, setActiveUrl] = useState<string | null>(resolvedUrl);
  const isCached = Boolean(activeUrl && loadedAvatarsCache.has(activeUrl));
  const [isLoading, setIsLoading] = useState<boolean>(!isCached);
  const [hasError, setHasError] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Sync state if src changes
  useEffect(() => {
    const nextUrl = src ? resolveCoverUrl(src) : null;
    setActiveUrl(nextUrl);
    if (nextUrl && loadedAvatarsCache.has(nextUrl)) {
      setIsLoading(false);
      setHasError(false);
    } else {
      setIsLoading(Boolean(nextUrl));
      setHasError(false);
    }
  }, [src]);

  // Check if browser already has it cached
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && activeUrl) {
      loadedAvatarsCache.add(activeUrl);
      setIsLoading(false);
    }
  }, [activeUrl]);

  const handleLoad = () => {
    if (activeUrl) loadedAvatarsCache.add(activeUrl);
    setIsLoading(false);
  };

  const handleError = () => {
    // Graceful proxy recovery: Try Worker proxy endpoint if not already tried
    if (activeUrl && !activeUrl.startsWith('data:') && !activeUrl.startsWith('blob:')) {
      const segments = activeUrl.split('/').filter(Boolean);
      const key = segments.length > 0 ? segments[segments.length - 1].split('?')[0] : '';
      if (key && key.includes('.')) {
        const apiBase = getWorkerApiBase();
        const proxyUrl = `${apiBase}/api/images/${encodeURIComponent(key)}`;

        if (!activeUrl.includes('/api/images/') && proxyUrl !== activeUrl) {
          setActiveUrl(proxyUrl);
          setIsLoading(true);
          setHasError(false);
          return;
        }
      }
    }
    setIsLoading(false);
    setHasError(true);
  };

  const roundedClass =
    shape === 'circle'
      ? 'rounded-full'
      : shape === 'square'
      ? 'rounded-none'
      : size === 'xs' || size === 'sm'
      ? 'rounded-lg'
      : 'rounded-2xl';

  const { container: defaultSizeClass, text: textSizeClass } = sizeClasses[size] || sizeClasses.md;
  const finalContainerClass = className ? className : defaultSizeClass;

  const displayInitial = (fallbackText || alt || 'U').charAt(0).toUpperCase();

  const hasValidImage = Boolean(activeUrl && !hasError);

  return (
    <div
      className={`relative overflow-hidden bg-slate-100 border border-slate-200/80 shrink-0 select-none flex items-center justify-center ${roundedClass} ${finalContainerClass}`}
    >
      {/* Skeleton Shimmer Loading Placeholder */}
      {hasValidImage && isLoading && !isCached && (
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse flex items-center justify-center pointer-events-none">
          <span className={`font-bold text-slate-300 ${textSizeClass}`}>{displayInitial}</span>
        </div>
      )}

      {/* Actual Avatar Image */}
      {hasValidImage ? (
        <img
          ref={imgRef}
          src={activeUrl!}
          alt={alt ? `Foto profil: ${alt}` : 'Foto profil pengguna UIBN'}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className="w-full h-full object-cover"
        />
      ) : fallbackIcon ? (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-tr from-indigo-500/20 to-sky-400/20 text-indigo-600">
          {fallbackIcon}
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-tr from-indigo-600 to-sky-500 text-white font-extrabold shadow-inner">
          <span className={textSizeClass}>{displayInitial}</span>
        </div>
      )}
    </div>
  );
};
